import React, { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';

import CrossIcon from '@/assets/icons/modal-cross-icon-privacy-preference.svg';
import Button from '@/components/V2/Button';
import Modal from '@/components/V2/Modal';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { useAppDispatch, useAppSelector } from '@/store';
import { loadCookieConsentAsync, setCookieConsentAsync } from '@/store/slices/appConfig';

import ToggleSwitch from '../BasicInformation/ToggleSwitch';

interface NewPrivacyPreferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewPrivacyPreferenceModal = ({ isOpen, onClose }: NewPrivacyPreferenceModalProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { generateURL, webalias } = useWebAlias();
  const isCookieConsentRejected = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [checked, setChecked] = useState<boolean>(isCookieConsentRejected ?? false);

  useEffect(() => {
    if (!isOpen) return;
    dispatch(loadCookieConsentAsync()).unwrap();
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setChecked(isCookieConsentRejected ?? false);
  }, [isCookieConsentRejected, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Reset scroll position after FocusTrap sets initial focus
    const timeoutId = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  const handleSave = () => {
    const wasRejected = isCookieConsentRejected ?? false;
    const willBeRejected = checked;

    dispatch(setCookieConsentAsync(checked));
    onClose();

    // If transitioning from rejected to accepted (opt-in), reload the page
    if (wasRejected && !willBeRejected) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  const navigateToPrivacyPreferencePage = () => {
    const privacyPreferencePath = generateURL('/account/privacypreference', webalias);

    if (pathname.includes('/account/login')) {
      const returnUrl = `${window.location.origin}${privacyPreferencePath}`;
      const loginUrl = `${pathname}?ReturnUrl=${encodeURIComponent(returnUrl)}`;
      onClose();
      router.push(loginUrl);
    } else {
      router.push(privacyPreferencePath);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="!v2-max-w-[992px] v2-w-full !v2-p-0 v2-rounded-2xl v2-bg-[#ffffff]"
      containerClassName="v2-flex v2-justify-center v2-items-center"
      closeBtnClassName="!v2-hidden"
    >
      <div className="v2-flex v2-items-center v2-justify-between v2-gap-4 v2-px-4 md:v2-px-6 v2-py-6">
        <div className="v2-flex v2-items-center v2-gap-3">
          <span className="v2-text-lg v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-[#101828] v2-leading-[27px]">
            PawTree
          </span>
        </div>
        <Image src={CrossIcon} height={32} width={32} alt="" role="button" onClick={() => onClose()} />
      </div>

      <div className="v2-border-t v2-border-[#E3E8E5]" />
      <div
        ref={scrollRef}
        className="v2-flex v2-flex-col v2-gap-6 v2-px-4 md:v2-px-6 v2-py-6 v2-overflow-auto v2-max-h-[55dvh] custom-scroll"
      >
        <div className="v2-space-y-3">
          <h2 className="v2-text-xl sm:v2-text-2xl v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-[#101828]">
            Your Privacy Choices
          </h2>
          <p className="v2-text-sm v2-font-sweet-sans-pro v2-text-[#101828]">
            This website uses cookies and other similar technologies (referred to as “cookies” below), including some
            with tracking functionality, to enhance user experience and to analyze performance and traffic on our
            website. These cookies may constitute a “sale” of personal information under certain state laws. These
            cookies allow us to analyze how visitors use our website and to measure website performance, and they also
            allow us to provide a quality experience by customizing our offering and quickly identifying and addressing
            issues that arise. Certain cookies and similar technologies may also share information about your use of our
            website with social media, advertising and/or analytics partners. Further information is available in our
            Privacy Policy and Cookie Policy.
          </p>
          <p className="v2-text-sm v2-font-sweet-sans-pro v2-text-[#101828]">
            This website honors Global Privacy Controls (GPCs), or opt-out preference signals. If your browser GPC is
            &quot;on,&quot; we interpret it as a request for that browser to opt-out of sharing for third-party targeted
            advertising and to opt-out of &quot;selling&quot; of personal information. To opt-out of sharing for
            third-party targeted advertising and to opt-out of &quot;selling&quot; of personal information, you can
            adjust the toggles below.
          </p>
          <p className="v2-text-xs v2-font-sweet-sans-pro v2-text-[#101828]">
            Note: If you opt-out of certain cookies, you may continue to see online (non-targeted) advertising from us
            on other websites and online services. In addition, because certain opt-outs rely on cookies, if you delete
            cookies from your browser, you may need to opt-out again.
          </p>
        </div>
        <div className="v2-flex v2-items-start">
          <div>
            <h3 className="v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-base v2-text-[#101828] v2-mb-1">
              Essential Cookies
            </h3>
            <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#101828]">
              These cookies are essential for the proper operation of the website. For example, they may facilitate page
              navigation or assist with security checks.
            </p>
          </div>
          <p className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-sm v2-text-[#123E2E] v2-flex-none">
            Always On
          </p>
        </div>
        <div className="v2-flex v2-items-start v2-justify-between v2-gap-4">
          <div className="v2-space-y-1">
            <h3 className="v2-text-sm sm:v2-text-base v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-[#101828]">
              Analytics/Performance Cookies
            </h3>
            <p className="v2-text-xs sm:v2-text-sm v2-font-sweet-sans-pro v2-text-[#101828]">
              These cookies may constitute a “sale” of personal information under certain state laws. These cookies
              allow us to analyze how visitors use our website and to measure website performance, and they also allow
              us to provide a quality experience by customizing our offering and quickly identifying and addressing
              issues that arise.
            </p>
          </div>
          <div className="v2-shrink-0">
            <ToggleSwitch
              checked={checked}
              onChange={setChecked}
              wrapperClassName={checked ? '!v2-h-6 !v2-w-11 !v2-bg-[#154734]' : '!v2-h-6 !v2-w-11 !v2-bg-[#C5D1CC]'}
              className={`!v2-w-5 !v2-h-5 v2-top-[1px] v2-translate-y-[1px] ${
                checked ? '!v2-translate-x-[22px]' : '!v2-translate-x-[2px]'
              }`}
            />
          </div>
        </div>

        <div className="v2-border-t v2-border-[#E3E8E5] v2-pt-4">
          <h3 className="v2-text-base v2-font-sweet-sans-pro-semi-bold v2-text-[#101828] v2-mb-3">
            Access, Correct & Delete Your Data
          </h3>
          <p className="v2-text-sm v2-font-sweet-sans-pro v2-text-[#101828]">
            You have the right to request a copy of your personal data, correct inaccuracies, or request deletion. Log
            into your PawTree account or contact us directly. Contact support on +1-800-672-4399.
          </p>
          <Button
            type="button"
            scheme="text"
            variant="filled"
            btnSize="sm"
            className="!v2-p-0 v2-mt-6 !v2-text-[#123E2E] !v2-underline !v2-bg-transparent !v2-text-sm !v2-font-sweet-sans-pro-medium !v2-font-medium "
            onClick={navigateToPrivacyPreferencePage}
          >
            Already have an account?
          </Button>
        </div>
      </div>
      <div className="v2-flex v2-flex-col v2-bg-[#F9FAFB] sm:v2-flex-row v2-items-stretch sm:v2-items-center v2-justify-between v2-rounded-b-xl v2-border-t v2-border-[#D0D5DD] v2-gap-4 v2-px-4 md:v2-px-6 v2-py-6">
        <p className="v2-text-xs sm:v2-text-sm v2-font-sweet-sans-pro v2-text-[#101828]">
          Your choice is saved to this browser via a cookie.
        </p>
        <Button
          type="button"
          scheme="primary"
          variant="filled"
          btnSize="lg"
          className="!v2-font-sweet-sans-pro-semi-bold !v2-text-base !v2-font-semibold !v2-h-[48px]"
          onClick={handleSave}
        >
          Save Preferences
        </Button>
      </div>
    </Modal>
  );
};

export default NewPrivacyPreferenceModal;
